import { defineComponent } from 'vue'
import type { DialogServiceMethods } from 'primevue/dialogservice'
import Button from 'primevue/button'
import { createDeferred } from '@/utils/deferred'
import './sidebar-dialogs.css'

export type DeleteSectionChoice = 'keep' | 'delete'

/**
 * Asks what happens to a section's items when the section is deleted: moved out to the top level
 * ('keep', the default) or deleted with it. Resolves to null when cancelled.
 *
 * `hiddenCount` is the number of items the person cannot currently open (and so cannot see).
 * They go wherever the visible ones go, so the prompt says they exist.
 */
export function promptDeleteSidebarSection (
  dialog: DialogServiceMethods,
  { title, visibleCount, hiddenCount }: { title: string, visibleCount: number, hiddenCount: number }
): Promise<DeleteSectionChoice | null> {
  const deferred = createDeferred<DeleteSectionChoice | null>()

  function finish (choice: DeleteSectionChoice | null) {
    if (!deferred.isPending) {
      return
    }
    deferred.resolve(choice)
    dialogRef.close()
  }

  const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`

  const DialogContent = defineComponent({
    setup () {
      return () => (
        <div class="sidebar-delete-section">
          <p>
            “{title}” has {plural(visibleCount, 'item')}.
            {hiddenCount > 0 && ` It also holds ${plural(hiddenCount, 'item')} for pages you can't currently open.`}
          </p>
          <p>Move them out to the top of the sidebar, or delete them along with the section?</p>
          <div class="sidebar-editor-footer">
            <Button type="button" size="small" label="Cancel" severity="secondary" text onClick={() => finish(null)} />
            <Button type="button" size="small" label="Delete section and items" severity="danger" outlined onClick={() => finish('delete')} />
            <Button type="button" size="small" label="Move items out" onClick={() => finish('keep')} autofocus />
          </div>
        </div>
      )
    }
  })

  const dialogRef = dialog.open(DialogContent, {
    props: {
      header: 'Delete section',
      style: { width: 'min(480px, calc(100vw - 2rem))' },
      pt: { root: 'sidebar-dialog-shell' },
      modal: true,
      closable: true,
      dismissableMask: true,
      closeOnEscape: true
    },
    onClose () {
      if (deferred.isPending) {
        deferred.resolve(null)
      }
    }
  })

  return deferred.promise
}
